import { StationData } from 'domain/models';
import { Resolution } from 'domain/models/api.models';
import { ConvertedData } from 'domain/models/data.models';

/**
 * Convert from:
 * {
 *   data: [
 *     {
 *
 *     }
 *   ]
 * }
 *
 * -->
 * {
 *   name: '05:00',
 *   hr: 98,
 *   temp: 1,
 *   precip: 0,
 *   viento: 2,
 *   rachas: 10,
 * }
 */

/**
 * Converts from StationData to ConvertedData.
 * Also, add null values when there is a gap (more than 1 day)
 * @param rawData
 * @returns
 */
const GAP_CONST = 4; // 4 Times the normal gap
export const useConvertData = (
	rawData: StationData[]
): [ConvertedData[], Set<string>] => {
	const availableVariables = new Set<string>();

	// Allow to merge
	if (!rawData?.length) {
		return [[], availableVariables];
	}

	let raw: ConvertedData[] = Object.values(
		rawData.reduce((acc, data) => {
			const { timestamp, data: stationData } = data;

			const key = new Date(timestamp).getTime();

			if (!acc[key]) {
				acc[key] = {
					ts: key,
					leafwetness: stationData.leafwetness,
					ir: stationData.ir,
					hurs: stationData.hurs,
					tas: stationData.tas,
					pr: stationData.pr,
					sfcwind: stationData.sfcwind,
					wgust: stationData.wgust,
					tas_cumulative: stationData.tas_cumulative,
					pr_cumulative: stationData.pr_cumulative,
					t_hour_cumulative: stationData.t_hour_cumulative,
				};
			} else {
				// Merge
				acc[key] = {
					...acc[key],
					leafwetness: stationData.leafwetness,
					ir: stationData.ir || acc[key].ir,
					hurs: stationData.hurs || acc[key].hurs,
					tas: stationData.tas || acc[key].tas,
					pr: stationData.pr || acc[key].pr,
					sfcwind: stationData.sfcwind || acc[key].sfcwind,
					wgust: stationData.wgust || acc[key].wgust,
					tas_cumulative: stationData.tas_cumulative || acc[key].tas_cumulative,
					pr_cumulative: stationData.pr_cumulative || acc[key].pr_cumulative,
					t_hour_cumulative:
						stationData.t_hour_cumulative || acc[key].t_hour_cumulative,
				};
			}

			// Add available variables
			Object.keys(stationData).forEach((key) => {
				if (stationData[key] !== null) {
					availableVariables.add(key);
				}
			});

			return acc;
		}, {} as { [ts: number]: ConvertedData })
	);

	// Order by timestamp
	raw = raw.sort((a, b) => a.ts - b.ts);

	// Calculate the median gap between observations, for each variable
	const medianGap = (raw || [])
		.map((d, i) => (i === 0 ? 0 : d.ts - raw[i - 1].ts))
		.sort()[Math.floor(raw.length / 2)];

	// Look for gaps
	const gaps = [];

	for (let i = 0; i < raw.length - 1; i++) {
		const elapsed = raw[i + 1].ts - raw[i].ts;
		if (elapsed > GAP_CONST * medianGap) {
			gaps.push(i);
		}
	}

	if (gaps.length === 0) {
		return [raw, availableVariables];
	}

	// Add null values
	const converted: ConvertedData[][] = [];
	let last = 0;
	for (const gap of gaps) {
		const slice: ConvertedData[] = raw.slice(last, gap + 1);
		converted.push(slice);
		last = gap + 1;
		// Add null values
		converted.push([
			{
				ts: raw[gap].ts + 1,
				leafwetness: null,
				ir: null,
				hurs: null,
				tas: null,
				pr: null,
				sfcwind: null,
				wgust: null,
				tas_cumulative: null,
				pr_cumulative: null,
				t_hour_cumulative: null,
			},
		]);
	}

	// Add last values
	converted.push(raw.slice(last));
	// Flatten
	return [converted.flat(), availableVariables];
};
